/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreateUpdateBaseComponent } from 'app/shared/components/create-update-base/create-update-base.component';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { ApiResponseSummary } from 'app/shared/models/api-info.model';
import { ConfirmationDialogService } from 'app/shared/services/confirmation-dialog.service';
import { passwordsMustMatch } from 'app/shared/validation/custom-validators';
import { RegexValidationStrings } from 'app/shared/validation/reg-ex-validation-constants';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { FormMode } from 'app/shared/enumeration/form-mode';
import { Router } from '@angular/router';
import { ResetPasswordModel } from '../models/reset-password-model';
import { ForgotPasswordService } from '../services/forgot-password-service';

@Component({
	selector: 'reset-password-form',
	templateUrl: './reset-password-form.component.html',
	styleUrls: ['./reset-password-form.component.scss'],
	standalone: false,
})
export class ResetPasswordFormComponent extends CreateUpdateBaseComponent<ResetPasswordModel> implements OnInit {
	hidePassword = true;

	public emailLinkToken: string = '';

	// shortcuts for validation
	get password(): AbstractControl {
		return this.dataForm.controls['password'];
	}
	get confirmPassword(): AbstractControl {
		return this.dataForm.controls['confirmPassword'];
	}

	constructor(
		protected fb: FormBuilder,
		private service: ForgotPasswordService,
		protected asideService: AsideService,
		protected formHelperService: ReactiveFormHelperService,
		protected confirmationDialogService: ConfirmationDialogService,
		private router: Router,
	) {
		super(fb, asideService, formHelperService, confirmationDialogService);
		this.data = { itemId: 0, formMode: FormMode.Create };
	}

	protected createFormGroup(): FormGroup<any> {
		return this.fb.group(
			{
				password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(12), Validators.pattern(RegexValidationStrings.PASSWORD)]],
				confirmPassword: ['', [Validators.required, Validators.maxLength(12), passwordsMustMatch()]],
			},
			{},
		);
	}

	protected populateControls(datafrom: FormGroup<any>) {}

	// overidden from CreateUpdateBaseComponent base class
	protected async loadData(id: number): Promise<ResetPasswordModel> {
		let dataModel: ResetPasswordModel;
		dataModel = new ResetPasswordModel();
		return Promise.resolve(dataModel);
	}

	protected createUpdateModel() {
		return {
			resetPasswordRequestToken: this.emailLinkToken,
			password: this.password.value,
		};
	}

	protected async doUpdate(id: number, dataToUpdate: any): Promise<ApiResponseSummary<any>> {
		throw 'Not supported.';
	}

	protected async doCreate(dataToUpdate: any): Promise<any> {
		return await this.service.submitResetPasswordRequest(dataToUpdate);
	}

	onSubmitResetPassword() {
		this.save();
	}

	// overidden from CreateUpdateBaseComponent base class
	// as this is an inline form, not an aside
	save() {
		if (this.formHelperService.isFormValid(this.dataForm)) {
			const updateModel = this.createUpdateModel();

			this.blockUI.start();
			this.doCreate(updateModel)
				.then((response: any) => {
					this.closeForm();
				})
				.finally(() => {
					this.blockUI.stop();
				});
		}
	}

	// overidden from CreateUpdateBaseComponent base class
	cancel() {
		// No confirmation requested.
		this.closeForm();
	}

	onCancel(event) {
		event.preventDefault();
		this.cancel();
	}

	closeForm() {
		// Rather than raising events, it may make more sense to reload the sign-in page, which will ensure it's in a clean state.
		//location.reload();
		// We want to lose the query string parameters when doing this.
		window.location.href = window.location.href.split('?')[0];
	}

	passwordFieldChanged() {
		if (this.confirmPassword.touched) this.confirmPassword.updateValueAndValidity();
	}
}
